/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../blueprint-classes/shared.enums';
import { IdNoteData } from './types/note-id-data.type';
import { Note } from './note.class';
import { NoteType } from './shared.enums';
import { Publish } from '../decorators';

import { ArtifactNoteData, NoteParams, NotesDataIn, NotesDataOut, UserNotesData } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../blueprint-classes/kitchen-design-base';


export class Notes extends KitchenDesignBase<NotesDataIn> {
	#contextEmail: string;
	readonly children: Array<Note>;

	constructor (contextEmail: string, options: KitchenDesignOptions<NotesDataIn> = {}) {
		super(BlueprintInstanceType.Notes, {});
		this.#contextEmail = contextEmail;

		this.children = [];
		if (options.dataIn && options.dataIn.children && options.dataIn.children.length > 0) {
			options.dataIn.children.forEach((n) => this.children.push(new Note(contextEmail, null, { dataIn: n })));
		}
		this.setInitialized();
	}

	@Publish('notes', 'added')
	public add (dataIn: Partial<NoteParams>): string | Note {
		const userEmail = dataIn?.createdBy ?? this.#contextEmail;
		const newNote = new Note(userEmail, { message: dataIn?.message ?? '', artifactId: dataIn.artifactId, type: dataIn.type || NoteType.NA });
		this.children.push(newNote);
		return newNote.id;
	}

	public getArtifactNotes (artifactId: string, type?: NoteType, options?: ArtifactNoteData): Array<Note> {
		const { includeDeleted = false, includeReplyChain = false, onlyRootNotes = false } = options ?? {};

		let artifactNotes = this.children.filter((note) => note.artifactId === artifactId &&
			(!type || note.type === type) &&
			((includeDeleted && note.deleted) || !note.deleted));

		if (onlyRootNotes) {
			artifactNotes = artifactNotes.filter((note) => !note.findParentInstanceOf(BlueprintInstanceType.Note));
		}

		if (includeReplyChain) {
			const allValidNotes = artifactNotes;
			artifactNotes.forEach((note) => {
				this.getAllChildNotes(note, allValidNotes);
			})
			artifactNotes = allValidNotes;
		}

		return artifactNotes;
	}

	private getAllChildNotes (note: Note, notes: Array<Note> = []): void {
		this.children.forEach((n) => {
			if (n.parent === note) {
				notes.push(n);
				this.getAllChildNotes(n, notes);
			}
		})
	}

	public getUserNotes (email: string, options?: UserNotesData, artifactId?: string): Array<Note> {
		const { includeDeleted = false, includeReplyChain = false, onlyRootNotes = false, anywhere = false } = options ?? {};

		const userNotes: Array<Note> = [];

		const checkAndAddNotes = (note: Note) => {
			const matchesUser = note.createdBy === email;
			const notDeleted = !note.deleted || (note.deleted && includeDeleted);
			const matchesArtifact = !artifactId || note.artifactId === artifactId;

			if (matchesUser && notDeleted && matchesArtifact) {
				userNotes.push(note);
			}

			if (anywhere && notDeleted) {
				this.children.forEach((n) => {
					if (n.parent === note) checkAndAddNotes(n);
				});
			}
		};

		this.children.forEach((note) => {
			if (anywhere || (!anywhere && !note.findParentInstanceOf(BlueprintInstanceType.Note))) checkAndAddNotes(note);
		});

		let validNotes = userNotes;

		if (onlyRootNotes) {
			validNotes = validNotes.filter((note) => !note.findParentInstanceOf(BlueprintInstanceType.Note));
		}

		if (includeReplyChain) {
			const allValidNotes = validNotes;
			validNotes.forEach((note) => {
				this.getAllChildNotes(note, allValidNotes);
			})
			validNotes = allValidNotes;
		}

		return validNotes;
	}

	public getId (id: string, options?: IdNoteData): Note | Array<Note> | null {
		const { includeReplyChain = false, onlyRootNotes = false } = options ?? {};

		const validNote = this.children.find((note: Note) => note.id === id);

		if (onlyRootNotes && validNote?.parent) {
			return null;
		}

		if (includeReplyChain && validNote) {
			const rootNote = validNote.findParentInstanceOf(BlueprintInstanceType.Note) as unknown as Note;

			const allValidNotes: Array<Note> = [];
			const collectAllNotes = (note: Note) => {
				allValidNotes.push(note);
				this.getAllChildNotes(note, allValidNotes);
			}
			collectAllNotes(rootNote);

			return allValidNotes;
		}

		return validNote ?? null;
	}

	public serialize (): NotesDataOut {
		return {
			children: this.children.map((note: Note) => note.serialize())
		};
	}
}
